/**
 * useArticleInteractions
 *
 * User-scoped article read/like state, backed by `/me/article-interactions`.
 * One row per (user, article) with nullable `readAt` and `likedAt` timestamps.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

export type ArticleInteraction = NonNullable<
  ResponseBody<paths["/me/article-interactions"]["get"]>["data"]
>[number];

type UpsertBody = RequestBody<
  paths["/me/article-interactions/{articleId}"]["put"]
>;

export const articleInteractionsKey = ["article-interactions"] as const;

export function useArticleInteractions() {
  const { client, patientId } = useApiClient();
  return useQuery<ArticleInteraction[]>({
    queryKey: articleInteractionsKey,
    meta: { persist: true },
    // /me/* is patient-scoped; without an authenticated patient the call
    // can only 401 and floods the console pre-auth (#1129).
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error, response } = await client.GET(
        "/me/article-interactions",
      );
      if (error) {
        console.error("[useArticleInteractions] GET error:", error);
        throwApiError(
          error,
          "Failed to load article interactions",
          "useArticleInteractions",
          response?.status,
        );
      }
      return data?.data ?? [];
    },
  });
}

export function useUpsertArticleInteraction() {
  const { client } = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      articleId,
      body,
    }: {
      articleId: string;
      body: UpsertBody;
    }) => {
      const { data, error, response } = await client.PUT(
        "/me/article-interactions/{articleId}",
        { params: { path: { articleId } }, body },
      );
      if (error) {
        console.error("[useUpsertArticleInteraction] PUT error:", error);
        throwApiError(
          error,
          "Failed to save article interaction",
          "useUpsertArticleInteraction",
          response?.status,
        );
      }
      return data?.data;
    },
    onMutate: async ({ articleId, body }) => {
      await qc.cancelQueries({ queryKey: articleInteractionsKey });
      const previous = qc.getQueryData<ArticleInteraction[]>(
        articleInteractionsKey,
      );
      const now = new Date().toISOString();

      qc.setQueryData<ArticleInteraction[]>(
        articleInteractionsKey,
        (current) => {
          const list = current ?? [];
          const idx = list.findIndex((r) => r.articleId === articleId);

          if (idx === -1) {
            // New row — fabricate optimistic placeholder. Server response
            // will replace `id`/`userId`/timestamps in onSuccess.
            const next: ArticleInteraction = {
              id: `optimistic-${articleId}`,
              userId: "optimistic",
              articleId,
              readAt: body.readAt === undefined ? null : body.readAt,
              likedAt: body.likedAt === undefined ? null : body.likedAt,
              createdAt: now,
              updatedAt: now,
              updatedBy: null,
            };
            return [...list, next];
          }

          // Existing row — patch in place, preserving fields not in body.
          const existing = list[idx];
          if (!existing) return list;
          const updated: ArticleInteraction = {
            ...existing,
            readAt: body.readAt === undefined ? existing.readAt : body.readAt,
            likedAt:
              body.likedAt === undefined ? existing.likedAt : body.likedAt,
            updatedAt: now,
          };
          const next = [...list];
          next[idx] = updated;
          return next;
        },
      );

      return { previous };
    },
    onSuccess: (updated) => {
      // Replace the optimistic placeholder with the server's authoritative row.
      if (!updated) return;
      qc.setQueryData<ArticleInteraction[]>(
        articleInteractionsKey,
        (current) => {
          const list = current ?? [];
          const idx = list.findIndex((r) => r.articleId === updated.articleId);
          if (idx === -1) return [...list, updated];
          const next = [...list];
          next[idx] = updated;
          return next;
        },
      );
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(articleInteractionsKey, context.previous);
      }
    },
  });
}
